import React from 'react';
import type { UnitRow } from '../data/joinCore';
import type { Parcel } from '../data/useParcels';
import { LensDef, catColor, LENSES } from '../data/lenses';
import { T } from '../theme/tokens';
import { PanelCol, StagePill, PctCell } from './PanelGrid';
import { safeUrl } from '../util/safeUrl';

/** Lens-aware grid columns: the active Color-by lens becomes a column (its value per unit), plus
 *  one column per active Split-by lens. Reuses the SAME cat()/catColor() the map + breakdown use,
 *  so a unit's grid pill always matches its map dot. Values are UNIT-GRAINED (a multi-unit lot's
 *  units each show their own value), falling back to the lot/parcel value for lot-level lenses. */

// Long lens labels → compact headers that fit a ~100px column.
const ABBREV: Record<string, string> = {
  'Contract Status': 'Contract',
  'Construction Upgrades': 'Constr. Upgrades',
  'Additional Contingencies': "Add'l Conting.",
  'Financial Incentives': 'Fin. Incentives',
  'Final Sales Price': 'Sales Price',
  'Master Build Stage': 'Master Stage',
  'Unit Model Version': 'Model Ver.',
  'House Orientation': 'Orientation',
  'Concurrency Phase': 'Concurrency',
};
const headerFor = (lens: LensDef) => ABBREV[lens.label] || lens.label;

// UNIT-grained accessors — read the unit's OWN field, so two units on one lot differ correctly.
// Lenses absent here fall back to lens.cat(parcel) (lot-level, or unit-level not carried on UnitRow).
const NUM_OF: Record<string, (u: UnitRow) => number | null> = {
  value: u => u.finalValue,
  finalSalesPrice: u => (u.finalSalesPrice ?? null),
  pctCO: u => u.percentComplete,
  buildOrder: u => u.buildOrder,   // global build rank — shown as "#N", not a heat band, when split-by
};
const CAT_OF: Record<string, (u: UnitRow) => string> = {
  buildStatus: u => u.buildStatus,
  model: u => u.model,
  package: u => u.pkg,
  version: u => u.version,
  modelVersion: u => u.modelVersion,
  constructionUpgrades: u => u.constructionUpgrade,
  ghlContract: u => u.contractStatusLive,   // unit-grained live contract status (so multi-unit lots differ)
};
const isNumericLens = (lens: LensDef) => lens.id in NUM_OF;

const fmtUSD = (n: number) =>
  n >= 1e6 ? '$' + (n / 1e6).toFixed(2).replace(/\.?0+$/, '') + 'M'
  : n >= 1e3 ? '$' + Math.round(n / 1e3) + 'k'
  : '$' + Math.round(n);

// Exact dollars (fees are small — $250.00, not the k/M heat buckets fmtUSD uses).
const fmtMoney = (n: number) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 });

// Permit milestone dates arrive as raw strings ('' = none). Bare YYYY-MM-DD gets a noon anchor so
// the local-tz render never slips a day. Compact M/D/YY for the narrow grid columns.
const fmtDate = (s: string): string => {
  if (!s) return '';
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(s) ? s + 'T12:00:00' : s;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return s;
  return (d.getMonth() + 1) + '/' + d.getDate() + '/' + String(d.getFullYear()).slice(2);
};
const dateRank = (s: string): number => { if (!s) return 0; const iso = /^\d{4}-\d{2}-\d{2}$/.test(s) ? s + 'T12:00:00' : s; const d = new Date(iso); return isNaN(d.getTime()) ? 0 : d.getTime(); };
const DateCell = ({ s }: { s: string }) => { const t = fmtDate(s); return t ? <span className="num" style={{ fontSize: 11, color: T.ink }}>{t}</span> : <span style={{ color: T.inkSoft }}>—</span>; };

/** Unit-grained categorical value for a lens (the lens's display string for this unit). */
function catVal(lens: LensDef, u: UnitRow, parcelOf: (u: UnitRow) => Parcel | undefined): string {
  const f = CAT_OF[lens.id];
  if (f) return f(u) || '—';
  const p = parcelOf(u);
  return (p ? lens.cat(p) : '—') || '—';
}
const numVal = (lens: LensDef, u: UnitRow): number | null => (NUM_OF[lens.id] ? NUM_OF[lens.id](u) : null);

/** Sort rank per category value: lens.order when defined, else by FREQUENCY in view (desc) so the
 *  dominant categories cluster; '—' / blank always sort last. Returns rank index (lower = first). */
function valueRank(lens: LensDef, units: UnitRow[], parcelOf: (u: UnitRow) => Parcel | undefined): Map<string, number> {
  if (lens.order && lens.order.length) return new Map(lens.order.map((v, i) => [v, i]));
  const cnt = new Map<string, number>();
  for (const u of units) { const v = catVal(lens, u, parcelOf); cnt.set(v, (cnt.get(v) || 0) + 1); }
  const sorted = [...cnt.entries()].filter(([v]) => v && v !== '—').sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  return new Map(sorted.map(([v], i) => [v, i]));
}
const BLANK_RANK = 1e6;

/** A multi-value cell: first token as a pill + "+N", full set on hover. Matches the map, which
 *  colours a lot by its first/primary token. */
function MultiPill({ raw, lens }: { raw: string; lens: LensDef }) {
  const toks = raw && raw !== '—' ? Array.from(new Set(raw.split(lens.sep || ',').map(s => s.trim()).filter(Boolean))) : [];
  if (!toks.length) return <span style={{ color: T.inkSoft }}>—</span>;
  return (
    <span title={toks.join(' · ')} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%', overflow: 'hidden' }}>
      <StagePill stage={toks[0]} color={catColor(lens, toks[0])} />
      {toks.length > 1 && <span style={{ flex: '0 0 auto', fontSize: 10, fontWeight: 600, color: T.inkSoft }}>+{toks.length - 1}</span>}
    </span>
  );
}

/** Columns for ONE lens (primary or a split). Build Stage gets the stage pill + a % column;
 *  $ / % lenses get a right-aligned numeric; everything else a tinted category pill. */
function colsForOneLens(lens: LensDef, units: UnitRow[], parcelOf: (u: UnitRow) => Parcel | undefined, isPrimary: boolean): PanelCol<UnitRow>[] {
  // Build Stage — preserve the stage pill + dedicated % column.
  if (lens.id === 'buildStatus') {
    // Untracked (no Build Stage yet) = not started = 0% — the same convention the build-% rollup
    // uses — so every unit shows a number, never a bare "—" (owner: "% is missing 0% for units").
    const pctOf = (u: UnitRow): number => u.percentComplete ?? 0;
    return [
      { key: lens.id, header: headerFor(lens), width: 96, grow: isPrimary, sortable: true, sortVal: u => pctOf(u),
        render: u => <StagePill stage={u.buildStatus} color={catColor(LENSES.buildStatus, u.buildStatus)} />,
        exportVal: u => u.buildStatus || '', exportKind: 'text' },
      { key: lens.id + '_pct', header: '% Complete', width: 72, align: 'right', sortable: true, sortVal: u => pctOf(u),
        render: u => <PctCell pct={pctOf(u)} />, exportVal: u => pctOf(u), exportKind: 'percent' },
    ];
  }
  // Permit status — the lifecycle pill PLUS the permit dossier Eric scans when triaging by permit:
  // a hyperlink to the county portal, the three milestone dates, and the outstanding fee balance.
  // No % column here — % belongs to Build stage (owner: "it has the % complete twice for this setup").
  if (lens.id === 'permitStatus') {
    const rank = valueRank(lens, units, parcelOf);
    return [
      { key: lens.id, header: 'Permit status', width: 112, grow: isPrimary, sortable: true,
        sortVal: u => { const v = catVal(lens, u, parcelOf); if (!v || v === '—') return BLANK_RANK; const r = rank.get(v); return r == null ? BLANK_RANK - 1 : r; },
        render: u => { const raw = catVal(lens, u, parcelOf); return <StagePill stage={raw === '—' ? '' : raw} color={catColor(lens, raw)} />; },
        exportVal: u => { const v = catVal(lens, u, parcelOf); return v === '—' ? '' : v; }, exportKind: 'text' },
      { key: 'permit_link', header: 'Permit', width: 66,
        render: u => { const p = parcelOf(u); const url = safeUrl(p?.permitUrl); const num = p?.permitNumber;
          return url
            ? <a href={url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                 title={num ? `Open permit ${num} on the Glades County portal` : 'Open on the Glades County portal'}
                 style={{ fontSize: 11.5, fontWeight: 600, color: '#2563a8', textDecoration: 'none', whiteSpace: 'nowrap' }}>County&nbsp;↗</a>
            : <span style={{ color: T.inkSoft }}>—</span>; },
        exportVal: u => safeUrl(parcelOf(u)?.permitUrl), exportKind: 'url' },
      { key: 'permit_applied', header: 'Applied', width: 62, align: 'right', sortable: true,
        sortVal: u => dateRank(parcelOf(u)?.permitAppliedDate || ''), render: u => <DateCell s={parcelOf(u)?.permitAppliedDate || ''} />,
        exportVal: u => parcelOf(u)?.permitAppliedDate || '', exportKind: 'date' },
      { key: 'permit_issued', header: 'Issued', width: 62, align: 'right', sortable: true,
        sortVal: u => dateRank(parcelOf(u)?.permitIssuedDate || ''), render: u => <DateCell s={parcelOf(u)?.permitIssuedDate || ''} />,
        exportVal: u => parcelOf(u)?.permitIssuedDate || '', exportKind: 'date' },
      { key: 'permit_completed', header: 'Completed', width: 70, align: 'right', sortable: true,
        sortVal: u => dateRank(parcelOf(u)?.permitCompletedDate || ''), render: u => <DateCell s={parcelOf(u)?.permitCompletedDate || ''} />,
        exportVal: u => parcelOf(u)?.permitCompletedDate || '', exportKind: 'date' },
      { key: 'permit_due', header: 'Amount due', width: 80, align: 'right', sortable: true,
        sortVal: u => parcelOf(u)?.feeBalance ?? -1,
        render: u => { const v = parcelOf(u)?.feeBalance; if (v == null) return <span style={{ color: T.inkSoft }}>—</span>;
          return <span className="num" style={{ fontSize: 11.5, fontWeight: 600, color: v > 0 ? T.ink : T.inkSoft }}>{fmtMoney(v)}</span>; },
        exportVal: u => parcelOf(u)?.feeBalance ?? null, exportKind: 'money' },
    ];
  }
  // Numeric ($ / % / rank) — show the raw figure, not the heat bucket.
  if (isNumericLens(lens)) {
    const isPct = lens.id === 'pctCO';
    const isRank = lens.id === 'buildOrder';   // integer build-order rank → "#N", not currency
    const fmtNum = (v: number) => isPct ? Math.round(v <= 1 ? v * 100 : v) + '%' : isRank ? '#' + v : fmtUSD(v);
    return [{ key: lens.id, header: isPct ? '% Complete' : headerFor(lens), width: isPct ? 72 : isRank ? 64 : 86, align: 'right', sortable: true,
      sortVal: u => numVal(lens, u),
      render: u => { const v = numVal(lens, u); if (v == null) return <span style={{ color: T.inkSoft }}>—</span>;
        return <span className="num" style={{ fontSize: 11.5, fontWeight: 600, color: T.ink }}>{fmtNum(v)}</span>; },
      exportVal: u => numVal(lens, u), exportKind: isPct ? 'percent' : isRank ? 'text' : 'money' }];
  }
  // Categorical / multi-value pill.
  const rank = valueRank(lens, units, parcelOf);
  const multi = !!lens.multi;
  return [{ key: lens.id, header: headerFor(lens), width: multi ? 120 : 100, grow: isPrimary, sortable: true,
    sortVal: u => { const v = catVal(lens, u, parcelOf); if (!v || v === '—') return BLANK_RANK; const r = rank.get(v); return r == null ? BLANK_RANK - 1 : r; },
    render: u => { const raw = catVal(lens, u, parcelOf);
      return multi ? <MultiPill raw={raw} lens={lens} /> : <StagePill stage={raw === '—' ? '' : raw} color={catColor(lens, raw)} />; },
    // Multi-value cells export the full token set (the grid shows "first +N"); single = the value.
    exportVal: u => { const raw = catVal(lens, u, parcelOf); if (!raw || raw === '—') return '';
      return multi ? Array.from(new Set(raw.split(lens.sep || ',').map(s => s.trim()).filter(Boolean))).join(', ') : raw; }, exportKind: 'text' }];
}

/** Build the full dynamic column set: Lot (always) · Model (only when no split, for width) ·
 *  ‹primary lens› · ‹split 1›…‹split N›. */
export function columnsForLens(
  lensDef: LensDef,
  splitDefs: LensDef[],
  units: UnitRow[],
  parcelOf: (u: UnitRow) => Parcel | undefined,
): PanelCol<UnitRow>[] {
  const lotLabel = (u: UnitRow): string => (u.lotName || u.unitName || u.blockLot || '—').replace(/\s*\(model home\)\s*$/i, '');
  const cols: PanelCol<UnitRow>[] = [
    { key: 'lot', header: 'Lot', width: 60, frozen: true, sortable: true, sortVal: u => lotLabel(u),
      render: u => <span style={{ fontSize: 13, fontWeight: 600 }}>{u.modelHome ? '⭐ ' : ''}{lotLabel(u)}</span>,
      exportVal: u => (u.modelHome ? '★ ' : '') + lotLabel(u), exportKind: 'text' },
  ];
  // Model column only when no splits are active (reclaim its width for split columns), and never
  // when the primary lens IS Model (that would emit two columns keyed 'model').
  if (splitDefs.length === 0 && lensDef.id !== 'model') {
    cols.push({ key: 'model', header: 'Model', width: 72, sortable: true, sortVal: u => u.model,
      render: u => <span style={{ fontSize: 12, color: T.inkSoft }}>{u.model || '—'}</span>,
      exportVal: u => u.model || '', exportKind: 'text' });
  }
  cols.push(...colsForOneLens(lensDef, units, parcelOf, true));
  for (const s of splitDefs) cols.push(...colsForOneLens(s, units, parcelOf, false));
  // Dedupe so the SAME column never appears twice — by key (a lens that's both primary and a split)
  // AND by visible header (two different lenses that surface the same column, e.g. the standalone
  // "% Complete" heat lens alongside Build stage's own "% Complete"). Same title = same column to the
  // user, so the first wins and any later twin is dropped. Owner: "no column shown twice — e.g. %".
  const seenKey = new Set<string>();
  const seenHdr = new Set<string>();
  return cols.filter(c => {
    const h = c.header.trim().toLowerCase();
    if (seenKey.has(c.key) || seenHdr.has(h)) return false;
    seenKey.add(c.key); seenHdr.add(h);
    return true;
  });
}
